import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, MapPin, Wrench } from "lucide-react";

const STATUS_META = {
  REQUESTED: { label: "Awaiting technician", tone: "info" },
  ASSIGNED: { label: "Technician assigned", tone: "info" },
  ACCEPTED: { label: "Confirmed", tone: "info" },
  DECLINED_BY_TECH: { label: "Reassignment pending", tone: "warn" },
  ON_VISIT: { label: "Technician on visit", tone: "info" },
  ESTIMATED: { label: "Review estimate", tone: "warn" },
  APPROVED_BY_CUSTOMER: { label: "In progress", tone: "info" },
  REJECTED_BY_CUSTOMER: { label: "Estimate rejected", tone: "warn" },
  COMPLETED: { label: "Completed", tone: "ok" },
  CANCELLED: { label: "Cancelled", tone: "muted" },
  DISPUTED: { label: "Disputed", tone: "err" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "muted" };
  const map = { ok: "bg-emerald-100 text-emerald-900", info: "bg-sky-100 text-sky-800", warn: "bg-amber-100 text-amber-900", err: "bg-rose-100 text-rose-900", muted: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[meta.tone]}`}>{meta.label}</span>;
}

export default function ResidentTechnicianServices() {
  const [pilot, setPilot] = useState(null);
  const [category, setCategory] = useState("Electrical");
  const [bookings, setBookings] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ description: "", preferred_slot: "", address: "", phone: "" });
  const [current, setCurrent] = useState(null);
  const [events, setEvents] = useState([]);

  const refreshPilot = async (cat = category) => {
    try {
      const { data } = await api.get(
        "/pilot/bookings/preview-availability",
        { params: { city: "Boisar", category: cat } },
      );
      setPilot(data.match);
    } catch { setPilot(null); }
  };
  const refreshBookings = async () => {
    try {
      const { data } = await api.get("/pilot/bookings/mine");
      setBookings(data.bookings || []);
    } catch { setBookings([]); }
  };

  useEffect(() => { refreshPilot(); refreshBookings(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { refreshPilot(category); /* eslint-disable-next-line */ }, [category]);

  const openBooking = async (id) => {
    try {
      const { data } = await api.get(`/pilot/bookings/${id}`);
      setCurrent(data.booking);
      setEvents(data.events || []);
    } catch (e) { toast.error("Could not load booking"); }
  };

  const submit = async () => {
    if (!pilot || !pilot.allow_bookings && !pilot.preview_bookings_enabled) {
      toast.error("Booking is not enabled yet");
      return;
    }
    if (!draft.description.trim() || !draft.preferred_slot.trim() || !draft.address.trim() || !draft.phone.trim()) {
      toast.error("Please fill every field");
      return;
    }
    try {
      await api.post("/pilot/bookings", {
        city: "Boisar", category,
        description: draft.description, preferred_slot: draft.preferred_slot,
        address: draft.address, phone: draft.phone,
        visiting_fee_quoted: "150",
      });
      toast.success("Request submitted");
      setCreating(false);
      setDraft({ description: "", preferred_slot: "", address: "", phone: "" });
      await refreshBookings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    }
  };

  const decide = async (approved) => {
    try {
      await api.post(`/pilot/bookings/${current.booking_id}/estimate/decision`, { approved });
      toast.success(approved ? "Estimate approved" : "Estimate rejected");
      await refreshBookings();
      await openBooking(current.booking_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const cancel = async () => {
    const reason = window.prompt("Reason for cancellation?");
    if (reason === null) return;
    try {
      await api.post(`/pilot/bookings/${current.booking_id}/cancel`, { reason });
      toast.success("Booking cancelled");
      await refreshBookings();
      await openBooking(current.booking_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const complain = async () => {
    const reason = window.prompt("Describe the issue");
    if (!reason) return;
    try {
      await api.post(`/pilot/bookings/${current.booking_id}/complain`, { reason });
      toast.success("Complaint filed");
      await refreshBookings();
      await openBooking(current.booking_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not file complaint"); }
  };

  if (bookings === null || pilot === undefined) return <Loading />;
  const bookingsEnabled = pilot && (pilot.allow_bookings || pilot.preview_bookings_enabled);

  return (
    <div className="space-y-6" data-testid="resident-technician-services">
      <PageHeader title="Resident Technician Services" subtitle="Boisar pilot — Electrical and Plumbing. Preview flow; real payment collection is disabled." />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> {pilot ? pilot.name : "No active pilot"}</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          {pilot ? (
            <>
              <div>Covers: {pilot.region.city}, {pilot.region.district}, {pilot.region.state}</div>
              <div>Categories available: {pilot.categories.join(", ")}</div>
              <div>Visiting fee (DRAFT): ₹150 — adjusted against labour if work proceeds. Materials extra.</div>
              {!bookingsEnabled && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex gap-2 items-start" data-testid="resident-bookings-disabled">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <span>Bookings for this pilot are not open yet. You will be able to request a technician once the pilot is activated.</span>
                </div>
              )}
              {bookingsEnabled && (
                <div className="flex items-center gap-3">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-48" data-testid="resident-tech-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {pilot.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setCreating(true)} data-testid="resident-tech-new">Request a technician</Button>
                </div>
              )}
            </>
          ) : (
            <div>No active pilot covers your area yet.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">My Bookings</CardTitle></CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <div className="text-sm text-slate-500">No bookings yet.</div>
          ) : (
            <table className="w-full text-sm border" data-testid="resident-tech-bookings">
              <thead className="bg-slate-50"><tr><th className="p-2 text-left">Booked</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Technician</th><th className="p-2"></th></tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t">
                    <td className="p-2 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="p-2 text-xs">{b.category}</td>
                    <td className="p-2"><StatusBadge status={b.status} /></td>
                    <td className="p-2 text-xs">{b.technician_display || "—"}</td>
                    <td className="p-2 text-right"><Button variant="outline" size="sm" onClick={() => openBooking(b.booking_id)} data-testid={`resident-tech-open-${b.booking_id}`}>View</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Request a {category} technician</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Describe the problem</Label>
              <Textarea className="h-20" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="resident-tech-desc" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Preferred slot</Label>
                <Input type="datetime-local" value={draft.preferred_slot} onChange={(e) => setDraft({ ...draft, preferred_slot: e.target.value })} data-testid="resident-tech-slot" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="+91…" data-testid="resident-tech-phone" />
              </div>
            </div>
            <div>
              <Label>Service address</Label>
              <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} data-testid="resident-tech-address" />
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              Visiting fee is ₹150 if the technician visits but the customer declines further work. If work proceeds, the ₹150 is adjusted against labour so you are not double charged. Taxes apply per the confirmed tax configuration. Draft commercial terms, preview only.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} data-testid="resident-tech-cancel">Cancel</Button>
            <Button onClick={submit} data-testid="resident-tech-submit">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!current} onOpenChange={(o) => !o && setCurrent(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Booking · {current?.category}</DialogTitle></DialogHeader>
          {current && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3"><StatusBadge status={current.status} /><span className="text-slate-500 text-xs">{current.booking_id}</span></div>
              <div className="rounded-md border p-3 space-y-1">
                <div><b>Description:</b> {current.description}</div>
                <div><b>Preferred slot:</b> {current.preferred_slot}</div>
                <div><b>Address:</b> {current.resident_address}</div>
                <div><b>Technician:</b> {current.technician_display || "—"}</div>
              </div>
              {current.estimate && (
                <div className="rounded-md border p-3 space-y-1">
                  <div className="font-medium">Estimate</div>
                  <div>Visiting fee: ₹{current.estimate.visiting_fee} (adjustment ₹{current.estimate.visiting_fee_adjustment}; net ₹{current.estimate.net_visiting_charged})</div>
                  <div>Labour: ₹{current.estimate.labour}</div>
                  <div>Materials ({current.estimate.materials?.length || 0}): ₹{current.estimate.materials_total}</div>
                  <div>Taxes: ₹{current.estimate.taxes}</div>
                  <div className="font-medium">Grand total: ₹{current.estimate.grand_total}</div>
                  {current.estimate.notes && <div className="text-xs text-slate-600 pt-1">Note: {current.estimate.notes}</div>}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {current.status === "ESTIMATED" && (
                  <>
                    <Button onClick={() => decide(true)} data-testid="resident-tech-approve"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                    <Button variant="outline" onClick={() => decide(false)} data-testid="resident-tech-reject">Reject</Button>
                  </>
                )}
                {["REQUESTED", "ASSIGNED", "ACCEPTED", "ON_VISIT", "DECLINED_BY_TECH"].includes(current.status) && (
                  <Button variant="outline" onClick={cancel} data-testid="resident-tech-cancel-booking">Cancel booking</Button>
                )}
                {["COMPLETED"].includes(current.status) && (
                  <Button variant="outline" onClick={complain} data-testid="resident-tech-complain">File complaint</Button>
                )}
              </div>
              <div className="rounded-md border p-3 space-y-1">
                <div className="font-medium flex items-center gap-2"><Clock className="h-4 w-4" /> Activity</div>
                {events.map((e) => (
                  <div key={e.event_id} className="text-xs text-slate-600"><span className="font-mono">{new Date(e.ts).toLocaleTimeString()}</span> · {e.event}{e.data?.reason ? ` — ${e.data.reason}` : ""}</div>
                ))}
              </div>
              <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900 flex gap-2 items-start"><Wrench className="h-4 w-4 mt-0.5" /><span>Preview flow. No payment is collected and no earnings are credited.</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
